import type { SxProps } from '@mui/system';
import type { FC } from 'react';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { EnlargeImageGallery } from '@pflegenavi/transaction-components';
import {
  DeleteDialog,
  Iconify,
  useFileUrlToTypeMapping,
} from '@pflegenavi/web-components';
import {
  Badge,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { UseReceiptImagesType } from '@pflegenavi/shared-frontend/platform';
import { useUrlsToImages } from '@pflegenavi/shared-frontend/platform';

interface ReceiptBatchImagesRowProps {
  count: number;
  batchImageIds: string[];
  handleDelete?: () => void;
  sx?: SxProps;
  useReceiptImages: UseReceiptImagesType;
  autoGenerateReceipts?: boolean;
  handleCopyReceipts?: () => void;
}

export const ReceiptBatchImagesRow: FC<ReceiptBatchImagesRowProps> = (
  props
) => {
  const [galleryOpen, setGalleryOpen] = useState(false);

  const { receiptImages: receiptImageUrls, ImageUrlsLoading } = useUrlsToImages(
    {
      receiptImageIds: props.batchImageIds,
      useReceiptImages: props.useReceiptImages,
    }
  );

  const imageUrlToTypeMap = useFileUrlToTypeMapping(receiptImageUrls);

  return (
    <ReceiptBatchImagesRowView
      {...props}
      key={props.batchImageIds.length}
      GalleryComponent={
        <EnlargeImageGallery
          loading={ImageUrlsLoading}
          open={galleryOpen}
          handleClose={() => setGalleryOpen(false)}
          initialSelectedIndex={0}
          imageUrls={receiptImageUrls || []}
          imageUrlToTypeMap={imageUrlToTypeMap}
        />
      }
      setGalleryOpen={setGalleryOpen}
    />
  );
};

export interface ReceiptBatchImagesRowViewProps
  extends ReceiptBatchImagesRowProps {
  GalleryComponent: React.ReactNode;
  setGalleryOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ReceiptBatchImagesRowView: FC<ReceiptBatchImagesRowViewProps> = ({
  count,
  GalleryComponent,
  handleDelete,
  sx,
  setGalleryOpen,
  autoGenerateReceipts,
  handleCopyReceipts,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleShowGallery = useCallback(() => {
    if (count > 0) {
      setGalleryOpen(true);
    }
  }, [count, setGalleryOpen]);

  const showButtonCursorStyle = useMemo(() => {
    if (count > 0) {
      return 'pointer';
    }
    return 'default';
  }, [count]);

  if (autoGenerateReceipts) {
    return (
      <Stack
        direction="row"
        sx={{
          gap: 2,
          alignItems: 'center',
        }}
      >
        <Iconify
          width={45}
          height={45}
          icon={'mdi:receipt-text'}
          color={theme.palette.info.main}
        />
        <Stack>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
            }}
          >
            {t('receipts.receipt-will-be-auto-generated')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: 13.5,
              color: 'grey.600',
            }}
          >
            {t('receipts.you-can-preview-on-the-detail-page')}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  if (count === 0) {
    return null;
  }

  return (
    <>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 2.5,
          ...sx,
        }}
      >
        <Stack
          direction="row"
          sx={{
            gap: 2,
            alignItems: 'center',
          }}
        >
          <Badge
            badgeContent={count > 0 ? count : undefined}
            color="info"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <Stack
              onClick={handleShowGallery}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
                height: 40,
                width: 40,
                backgroundColor: 'grey.200',
                borderRadius: 2,
                cursor: showButtonCursorStyle,
              }}
            >
              {count > 0 ? (
                <Iconify
                  width={28}
                  height={28}
                  icon={'mdi:receipt-text'}
                  color={theme.palette.success.main}
                />
              ) : (
                <Iconify
                  width={28}
                  height={28}
                  icon={'mdi:receipt-text-remove'}
                  color={theme.palette.grey.A400}
                />
              )}
            </Stack>
          </Badge>
          <Stack>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
              }}
            >
              {t('receipts.receipt-uploaded')}
            </Typography>
            <Typography
              variant="caption"
              onClick={handleShowGallery}
              sx={{
                fontSize: 13.5,
                color: 'grey.600',
                cursor: showButtonCursorStyle,
                userSelect: 'none',
              }}
            >
              {count > 0
                ? t('actions.view')
                : t('receipts.batch.no-receipts-were-added')}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            gap: 1,
          }}
        >
          {handleCopyReceipts && (
            <Tooltip title={t('receipts.batch.use-as-individual-receipts')}>
              <IconButton
                sx={{
                  mt: -0.85,
                  mr: -1,
                }}
                onClick={handleCopyReceipts}
              >
                <Iconify width={20} height={20} icon={'eva:copy-outline'} />
              </IconButton>
            </Tooltip>
          )}
          {handleDelete && (
            <IconButton
              sx={{
                mt: -0.85,
                mr: -1,
              }}
              onClick={() => setDeleteModalOpen(true)}
            >
              <Iconify
                width={20}
                height={20}
                icon={'eva:trash-2-outline'}
                color={theme.palette.error.main}
              />
            </IconButton>
          )}
        </Stack>
      </Stack>
      {GalleryComponent}
      {handleDelete && (
        <DeleteDialog
          open={deleteModalOpen}
          handleDelete={handleDelete}
          setDeleteDialogOpen={setDeleteModalOpen}
          confirmationMessage={t(
            'receipts.batch.form.delete-batch-images-text'
          )}
        />
      )}
    </>
  );
};
